import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Spinner, Alert, Container, Row, Col, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api, { getPublicExpense } from '../services/api';
import { formatExpenseCategory } from '../utils/expenseCategories';
import './ReceiptPage.css';

const ExpenseReceiptPage = () => {
    const { public_id } = useParams();
    const { i18n, t } = useTranslation();
    const { language, changeLanguage } = i18n;
    const location = useLocation();

    const [expense, setExpense] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Helper to determine text color (black or white) based on background brightness
    const getContrastingColor = (hexColor) => {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        if (hex.length !== 6) return '#000000'; // Fallback

        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Luminance formula (Rec. 601)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    const textColor = getContrastingColor(expense?.invoice_color || '#ffffff');
    const mutedColor = textColor === '#FFFFFF' ? '#e0e0e0' : '#666666'; // Adjust muted text too

    useEffect(() => {
        const fetchExpense = async () => {
            try {
                const response = await getPublicExpense(public_id);
                setExpense(response.data);
            } catch (err) {
                setError('Expense receipt not found or expired');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchExpense();
    }, [public_id]);

    const options = expense?.invoice_display_options || {
        show_client_info: true,
        show_business_info: true,
        show_delivery_date: true,
        show_payment_history: true,
        show_observations: true,
        show_notes: true,
        show_qr_code: true
    };

    useEffect(() => {
        if (!loading && expense) {
            const queryParams = new URLSearchParams(location.search);
            if (queryParams.get('print') === 'true') {
                setTimeout(() => {
                    window.print();
                }, 500);
            }
        }
    }, [loading, expense, location.search]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(amount);
    };

    if (loading) {
        return <div className="receipt-wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner animation="border" /></div>;
    }

    if (error) {
        return <div className="receipt-wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Alert variant="danger">{error}</Alert></div>;
    }

    if (!expense) {
        return null;
    }

    // Determine supplier display info
    const supplierName = expense.supplier_name_joined || expense.supplier_name || 'N/A';
    const supplierPhone = expense.supplier_phone || '';
    const supplierAddress = expense.supplier_address || '';
    const supplierTaxId = expense.supplier_tax_id || '';

    return (
        <div className="receipt-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', padding: '20px 0' }}>
            <div className="receipt-container" style={{ backgroundColor: expense.invoice_color || '#fff', color: textColor }}>
                <div className="text-center mb-4">
                    {expense.logo_url && (
                        <img src={expense.logo_url} alt="Logo" className="receipt-logo mb-2" />
                    )}
                    <h4 className="fw-bold">{expense.business_name || t('invoicePage.yourBusiness')}</h4>
                    {expense.business_address && <div className="small" style={{ color: textColor }}>{expense.business_address}</div>}
                    {expense.business_phone && <div className="small" style={{ color: textColor }}>Tel: {expense.business_phone}</div>}
                    {expense.fiscal_id && (
                        <div style={{ fontSize: '0.85rem', color: mutedColor, marginTop: '2px' }}>
                            {expense.fiscal_id_type ? `${expense.fiscal_id_type}: ` : ''}{expense.fiscal_id}
                        </div>
                    )}
                    {expense.contact_email && <div className="small">{expense.contact_email}</div>}
                </div>

                <div className="border-top border-bottom py-2 mb-3" style={{ borderColor: mutedColor }}>
                    <h5 className="text-center fw-bold mb-0">COMPROBANTE DE EGRESO</h5>
                    <div className="text-center small" style={{ color: mutedColor }}>#{expense.expense_id}</div>
                </div>

                <div className="mb-3">
                    <Row className="mb-1">
                        <Col xs={4} className="fw-bold" style={{ color: textColor }}>Fecha:</Col>
                        <Col xs={8} className="text-end" style={{ color: textColor }}>{new Date(expense.date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</Col>
                    </Row>
                    <Row className="mb-1">
                        <Col xs={4} className="fw-bold" style={{ color: textColor }}>Proveedor:</Col>
                        <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierName}</Col>
                    </Row>
                    {supplierTaxId && (
                        <Row className="mb-1">
                            <Col xs={4} className="fw-bold" style={{ color: textColor }}>NIT/CC:</Col>
                            <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierTaxId}</Col>
                        </Row>
                    )}
                    {supplierPhone && (
                        <Row className="mb-1">
                            <Col xs={4} className="fw-bold" style={{ color: textColor }}>Teléfono:</Col>
                            <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierPhone}</Col>
                        </Row>
                    )}
                </div>

                <div className="mb-4">
                    <h6 className="fw-bold border-bottom pb-1 mb-2" style={{ color: textColor, borderColor: mutedColor }}>Detalles del Gasto</h6>
                    <Row className="mb-1">
                        <Col xs={12} className="fw-bold mb-1" style={{ color: textColor }}>{formatExpenseCategory(expense.category, t)}</Col>
                        <Col xs={12} className="small fst-italic mb-1" style={{ color: mutedColor }}>{expense.description || 'Sin descripción'}</Col>
                    </Row>
                    <Row className="mt-2 text-end">
                        <Col xs={12}>
                            <h4 className="fw-bold" style={{ color: textColor }}>{formatCurrency(expense.amount)}</h4>
                        </Col>
                    </Row>
                </div>

                <div className="border-top pt-2 mb-4" style={{ borderColor: mutedColor }}>
                    <Row className="mb-1">
                        <Col xs={5} className="fw-bold" style={{ color: textColor }}>Método de Pago:</Col>
                        <Col xs={7} className="text-end" style={{ color: textColor }}>{t(`expensesPage.paymentMethods.${expense.payment_method}`) || expense.payment_method || 'N/A'}</Col>
                    </Row>
                </div>

                <div className="receipt-footer text-center mt-4 pt-3 border-top">
                    {/* QR Code */}
                    {options.show_qr_code !== false && expense.qr_code_url && (
                        <div className="receipt-qr-code text-center mb-3">
                            <img
                                src={expense.qr_code_url}
                                alt="QR Code"
                                style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                            />
                        </div>
                    )}

                    <div className="small" style={{ color: mutedColor }}>Gracias por su servicio.</div>
                    {!expense.hide_powered_by && (
                        <div className="powered-by mt-2">
                            <small style={{ fontSize: '0.7rem', color: mutedColor }}>Powered by Evazard</small>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExpenseReceiptPage;
